import { useEffect, useRef, useState, Suspense } from 'react'
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { routes } from './routes.jsx'
import { PlayerProvider, usePlayer } from '../hooks/usePlayer.js'
import { useQuota } from '../hooks/useQuota.js'
import BottomNav from '../components/BottomNav.jsx'
import LegalGate from '../components/LegalGate.jsx'
import ClubGate from '../components/ClubGate.jsx'
import LangSync from '../components/LangSync.jsx'
import { avatarUrl, pingActivity } from '../api/levelApi.js'
import { isPasswordRecovery } from '../lib/recoveryFlag.js'
import { takeReturn } from '../lib/returnUrl.js'

const BASE = import.meta.env.VITE_LEVEL_API_BASE || ''

function useMaintenanceMode() {
  const [maintenance, setMaintenance] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    async function check() {
      try {
        const res = await fetch(`${BASE}/api/status`)
        if (res.ok) {
          const data = await res.json()
          setMaintenance(!!data.maintenance)
          setMessage(data.message || '')
        }
      } catch {}
    }
    check()
    const t = setInterval(check, 30000)
    return () => clearInterval(t)
  }, [])

  return { maintenance, message }
}

function MaintenanceScreen({ message }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#0e0e11', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 32, color: '#fff', textAlign: 'center'
    }}>
      <div style={{ fontSize: '3rem', marginBottom: 24 }}>🔧</div>
      <p style={{ margin: '0 0 12px', fontWeight: 800, fontSize: '1.4rem' }}>En mantenimiento</p>
      <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', maxWidth: 320 }}>
        {message || 'Actualización en curso. Vuelve en unos minutos.'}
      </p>
    </div>
  )
}

// Enlaces que se ocultan en la versión web (>= 841px, ver .nav-link-desktop-hidden
// en app.css): junto a los de administrador la barra se montaba sobre el nombre
// del usuario. Solo siguen en el menú del móvil.
// Informe se abre desde Progreso y Perfil, y Grupos desde Mi nivel.
const NAV_DESKTOP_HIDDEN = new Set(['/grupos', '/informe', '/board'])

const BASE_NAV_EXCLUDED = new Set([
  '/board', '/login', '/waitlist', '/confirmar', '/premium', '/perfil', '/onboarding',
  '/metrica/:metricKey', '/ranking', '/grupos/:id', '/grupo/unirse/:code',
  '/reto/:code', '/upload-tipos', '/plan', '/partidos', '/partido/:id', '/torneos', '/torneo/:id', '/torneo/:id/imprimir', '/club-ranking/:id', '/t/:code',
])

function buildNavItems(isAdmin, isTeacher) {
  return routes.filter(
    (route) =>
      !BASE_NAV_EXCLUDED.has(route.path) &&
      (route.path !== '/labeling' || isAdmin) &&
      (route.path !== '/admin'    || isAdmin) &&
      (route.path !== '/alumnos'  || isTeacher)
  )
}

function getInitial(name) {
  if (!name) return '?'
  return name.trim()[0].toUpperCase()
}

const FULLSCREEN_ROUTES = ['/informe']

function AppLayout() {
  const { t } = useTranslation()
  const { debouncedName, signOut, user, passwordRecovery, checkPasswordRecoveryHash } = usePlayer()
  const [avatarBroken, setAvatarBroken] = useState(false)
  useEffect(() => { setAvatarBroken(false) }, [user?.id])
  // Usuarios activos (Admin): al abrir la app y al volver a ella
  useEffect(() => {
    if (!user?.id) return
    pingActivity('app')
    const onVisible = () => { if (document.visibilityState === 'visible') pingActivity('app') }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [user?.id])
  const { isAdmin, isTeacher } = useQuota()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  // Cuadrante para imprimir: sin barras (se imprime la hoja tal cual)
  const isFullscreen = FULLSCREEN_ROUTES.includes(location.pathname) || /^\/torneo\/\d+\/imprimir$/.test(location.pathname)

  // PlayerProvider (donde vive `passwordRecovery`) nunca se remonta, así que
  // su lectura del hash puede quedarse anclada a como estaba la URL cuando
  // arrancó la app. useLocation() sí se actualiza en cada cambio de ruta
  // pase lo que pase por debajo (recarga completa o no) — reforzar aquí la
  // detección evita que un enlace de recuperación nuevo, abierto con la
  // pestaña ya lograda desde antes, se cuele sin que passwordRecovery llegue
  // a marcarse a tiempo para el efecto de onboarding de más abajo.
  useEffect(() => { checkPasswordRecoveryHash() }, [location, checkPasswordRecoveryHash])

  // Tras entrar o darse de alta (y el onboarding), todos acaban en /dashboard:
  // si venía de un enlace (torneo por WhatsApp, grupo), se le lleva allí
  useEffect(() => {
    if (!user?.id || location.pathname !== '/dashboard') return
    const back = takeReturn()
    if (back) navigate(back, { replace: true })
  }, [user?.id, location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // El enlace del correo puede acabar abriendo la app en una ruta distinta de
  // /login — sobre todo con la PWA instalada, que captura el enlace y arranca
  // en su propia ruta de inicio. Sin esto, el usuario acaba en el dashboard
  // (ya con sesión) y nunca ve el formulario de nueva contraseña.
  useEffect(() => {
    if (isPasswordRecovery() && location.pathname !== '/login') {
      navigate('/login' + window.location.search, { replace: true })
    }
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(event) {
      if (!menuOpen) return
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [menuOpen])

  useEffect(() => {
    let startX = 0
    let startY = 0

    function handleTouchStart(event) {
      const touch = event.touches?.[0]
      if (!touch) return
      startX = touch.clientX
      startY = touch.clientY
    }

    function handleTouchMove(event) {
      const touch = event.touches?.[0]
      if (!touch) return

      // Zonas que sí se deslizan en horizontal (p. ej. la guía de grabación):
      // sin esta excepción no se podían pasar sus pantallas en iPhone.
      if (event.target?.closest?.('[data-hswipe]')) return

      const dx = Math.abs(touch.clientX - startX)
      const dy = Math.abs(touch.clientY - startY)
      if (dx > dy) event.preventDefault()
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
    }
  }, [])

  // Redirect onboarding para usuarios nuevos
  useEffect(() => {
    // La sesión temporal de "restablecer contraseña" también deriva un
    // nombre de jugador — sin este guard, mandaba a /onboarding (y de ahí a
    // /dashboard) a cualquiera que abriera el enlace del correo sin haber
    // completado antes el onboarding en ese navegador (p.ej. incógnito).
    // isPasswordRecovery() en vez de leer el hash: supabase-js lo borra en
    // cuanto lo procesa, y ese borrado cuenta como cambio de ruta que vuelve
    // a disparar este efecto con la URL ya limpia (ver lib/recoveryFlag.js).
    if (!debouncedName || passwordRecovery || isPasswordRecovery()) return
    let done
    try {
      done = localStorage.getItem('level_onboarding_done')
    } catch {
      done = null
    }
    if (!done) {
      navigate('/onboarding')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName, passwordRecovery, location.hash])

  return (
    <div className="app-shell">
      <header className="topbar" style={isFullscreen ? { display: 'none' } : undefined}>
        <div className="brand">
          <a href="https://nivelaai.com" target="_blank" rel="noopener noreferrer" className="brand-logo-wrap">
            {/* public/brand: versión a 320 px (2x de los 160 px a los que se
                pinta; la original pesa 50 KB). Ruta fija para poder precargarla
                desde index.html: era lo último en aparecer al abrir la app. */}
            <img src="/brand/nivelaai-logo-320.png" alt="Nivela AI" className="brand-logo-img" width="160" height="35" fetchPriority="high" />
          </a>
          {debouncedName ? (
            <button
              className="topbar-avatar-wrap"
              type="button"
              onClick={() => navigate('/perfil')}
              aria-label="Ver perfil"
            >
              {user?.id && !avatarBroken ? (
                <img
                  className="topbar-avatar"
                  style={{ objectFit: 'cover' }}
                  src={avatarUrl(user.id)}
                  alt=""
                  onError={() => setAvatarBroken(true)}
                />
              ) : (
                <span className="topbar-avatar">{getInitial(debouncedName)}</span>
              )}
              <span className="topbar-avatar-name">{debouncedName}</span>
            </button>
          ) : null}
        </div>
        <div ref={menuRef} className="menu-shell">
          <button
            className="menu-toggle"
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Abrir menu"
          >
            <span />
            <span />
            <span />
          </button>
          <nav className={`nav ${menuOpen ? 'nav-open' : ''}`}>
            {buildNavItems(isAdmin, isTeacher).map((item) => (
              <NavLink
                key={item.path}
                to={item.navPath || item.path}
                className={({ isActive }) =>
                  `${isActive ? 'nav-link nav-link-active' : 'nav-link'}${NAV_DESKTOP_HIDDEN.has(item.path) ? ' nav-link-desktop-hidden' : ''}`
                }
                onClick={() => setMenuOpen(false)}
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
            <NavLink
              to="/board"
              className={({ isActive }) =>
                `${isActive ? 'nav-link nav-link-active' : 'nav-link'} nav-link-desktop-hidden`
              }
              onClick={() => setMenuOpen(false)}
            >
              {t('nav.board')}
            </NavLink>
            <button
              className="nav-link nav-link-exit"
              type="button"
              onClick={async () => {
                setMenuOpen(false)
                await signOut()
                navigate('/login')
              }}
            >
              {t('nav.exit')}
            </button>
          </nav>
        </div>
      </header>
      <main className="page">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            {routes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </main>
      {!isFullscreen && <BottomNav />}
      <LegalGate />
      <ClubGate />
      <LangSync />
    </div>
  )
}

export default function App() {
  const { maintenance, message } = useMaintenanceMode()
  // /admin siempre accesible aunque haya mantenimiento
  const isAdmin = window.location.pathname.startsWith('/admin')

  // Click-through tracking de push: si la app arranca con ?nid=<id> (lo
  // incrusta api/push.py en la url de cada notificación), se reporta el
  // click y se limpia la url. Funciona igual en web, Android/TWA e iOS
  // porque los tres cargan esta misma SPA — no hace falta tocar código
  // nativo (ver AppDelegate.swift/WebView.swift, que ya navegan el WebView
  // a la url de la notificación tal cual). 2026-08-11.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const nid = params.get('nid')
      if (nid) {
        fetch(`${BASE}/api/push/click/${encodeURIComponent(nid)}`).catch(() => {})
        params.delete('nid')
        const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash
        window.history.replaceState({}, '', clean)
      }
    } catch {}
  }, [])

  if (maintenance && !isAdmin) {
    return <MaintenanceScreen message={message} />
  }

  return (
    <PlayerProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </PlayerProvider>
  )
}
